import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Waves } from "lucide-react";

const Terms = () => {
  useEffect(() => {
    document.title = "Terms & Conditions — Barrelcast";
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border py-5 px-6">
        <Link to="/" className="inline-flex items-center gap-2 text-primary font-bold text-sm uppercase tracking-widest hover:opacity-80 transition-opacity">
          <Waves className="h-4 w-4" /> Barrelcast
        </Link>
      </header>

      {/* Content */}
      <main className="container max-w-3xl py-16 px-6">
        <h1 className="text-3xl font-bold mb-2">Terms &amp; Conditions</h1>
        <p className="text-sm text-muted-foreground mb-10">Last updated: May 2026</p>

        <div className="space-y-10 text-sm leading-7 text-foreground/80">

          <section>
            <h2 className="text-base font-semibold text-foreground mb-2">1. Acceptance of Terms</h2>
            <p>
              By accessing or using Barrelcast ("the Service"), you agree to be bound by these Terms and
              Conditions. If you do not agree with any part of these terms, you may not use the Service.
              Barrelcast is operated from South Africa and is governed by the laws of the Republic of
              South Africa.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-foreground mb-2">2. Description of Service</h2>
            <p>
              Barrelcast provides live surf condition data, swell forecasts, board recommendations, and
              related surfing information sourced from third-party APIs including Open-Meteo. The Service
              is provided free of charge for personal, non-commercial use.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-foreground mb-2">3. User Accounts</h2>
            <p>
              You may create a profile on Barrelcast. You are responsible for maintaining the
              confidentiality of your account credentials and for all activity that occurs under your
              account. You agree to provide accurate and current information when registering. We reserve
              the right to suspend or terminate accounts that violate these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-foreground mb-2">4. Accuracy of Information</h2>
            <p>
              Surf conditions, forecasts, and board recommendations provided by Barrelcast are for
              informational purposes only. We make no warranties as to the accuracy, completeness, or
              fitness for purpose of any data displayed. <strong className="text-foreground">Always check
              local conditions before entering the water.</strong> Barrelcast accepts no liability for
              decisions made based on information provided through the Service.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-foreground mb-2">5. Safety Disclaimer</h2>
            <p>
              Surfing is an inherently dangerous activity. Barrelcast does not assume any responsibility
              for injury, loss, or damage arising from your use of surf condition data or board
              recommendations. You surf at your own risk. Always respect local bylaws, beach flags, and
              the advice of qualified surf lifesavers.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-foreground mb-2">6. Intellectual Property</h2>
            <p>
              All content, branding, and code on Barrelcast is the property of Barrelcast and may not
              be reproduced, distributed, or used without prior written permission. Third-party data
              remains the property of the respective providers.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-foreground mb-2">7. Prohibited Conduct</h2>
            <p>You agree not to:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Use the Service for any unlawful purpose</li>
              <li>Attempt to gain unauthorised access to any part of the Service</li>
              <li>Scrape, copy, or redistribute data without permission</li>
              <li>Impersonate another person or entity</li>
              <li>Upload or transmit harmful, offensive, or misleading content</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-semibold text-foreground mb-2">8. Third-Party Services</h2>
            <p>
              Barrelcast integrates with third-party APIs and services (including Open-Meteo and Google
              Maps). We are not responsible for the availability, accuracy, or terms of those services.
              Your use of linked third-party services is subject to their own terms and conditions.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-foreground mb-2">9. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by South African law, Barrelcast shall not be liable for
              any direct, indirect, incidental, or consequential damages arising from your use of or
              inability to use the Service. This includes, without limitation, loss of data, personal
              injury, or property damage.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-foreground mb-2">10. Governing Law</h2>
            <p>
              These Terms are governed by and construed in accordance with the laws of the Republic of
              South Africa. Any disputes shall be subject to the jurisdiction of the South African courts.
              Where applicable, the Consumer Protection Act 68 of 2008 and the Electronic Communications
              and Transactions Act 25 of 2002 apply.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-foreground mb-2">11. Changes to Terms</h2>
            <p>
              We reserve the right to update these Terms at any time. Continued use of the Service after
              changes are posted constitutes your acceptance of the revised Terms. We will indicate the
              date of the most recent update at the top of this page.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-foreground mb-2">12. Contact</h2>
            <p>
              For questions about these Terms, please reach out via the Barrelcast website at{" "}
              <a href="https://barrelcast.surf" className="text-primary underline hover:opacity-80">
                barrelcast.surf
              </a>.
            </p>
          </section>

        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-8 text-center text-sm text-gray-300">
        <p>Made with salt &amp; stoke. Always check local conditions before paddling out.</p>
        <p className="mt-1">© Copyright 2026 Barrelcast. All rights reserved.</p>
        <p className="mt-2 flex justify-center gap-4">
          <Link to="/terms" className="underline hover:text-white transition-colors">Terms &amp; Conditions</Link>
          <Link to="/privacy" className="underline hover:text-white transition-colors">Privacy Policy</Link>
        </p>
      </footer>
    </div>
  );
};

export default Terms;
